/**
 * Offline Queue — IndexedDB-backed expense capture queue
 *
 * When offline: expenses are stored in IndexedDB.
 * When back online: queued expenses are synced to the backend.
 */

import { openDB, type IDBPDatabase } from 'idb';
import type { Expense } from '@/types';

const DB_NAME = 'nexus-offline';
const DB_VERSION = 1;
const EXPENSES_STORE = 'pending-expenses';
const RECEIPTS_STORE = 'pending-receipts';

interface QueuedExpense {
  localId: string;
  expense: Partial<Expense>;
  receiptBlob?: Blob;
  queuedAt: string;
  syncStatus: 'pending' | 'syncing' | 'failed';
  retryCount: number;
  lastError?: string;
}

async function getDB(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(EXPENSES_STORE)) {
        db.createObjectStore(EXPENSES_STORE, { keyPath: 'localId' });
      }
      if (!db.objectStoreNames.contains(RECEIPTS_STORE)) {
        db.createObjectStore(RECEIPTS_STORE, { keyPath: 'localId' });
      }
    },
  });
}

/** Queue an expense for later sync */
export async function queueExpense(expense: Partial<Expense>, receiptBlob?: Blob): Promise<string> {
  const db = await getDB();
  const localId = crypto.randomUUID();

  const queued: QueuedExpense = {
    localId,
    expense,
    receiptBlob,
    queuedAt: new Date().toISOString(),
    syncStatus: 'pending',
    retryCount: 0,
  };

  await db.put(EXPENSES_STORE, queued);
  return localId;
}

/** Get all pending expenses */
export async function getPendingExpenses(): Promise<QueuedExpense[]> {
  const db = await getDB();
  return db.getAll(EXPENSES_STORE);
}

/** Get count of pending expenses */
export async function getPendingCount(): Promise<number> {
  const db = await getDB();
  return db.count(EXPENSES_STORE);
}

/** Remove a synced expense from the queue */
export async function removeFromQueue(localId: string): Promise<void> {
  const db = await getDB();
  await db.delete(EXPENSES_STORE, localId);
}

/** Mark an expense as failed */
export async function markFailed(localId: string, error: string): Promise<void> {
  const db = await getDB();
  const item = await db.get(EXPENSES_STORE, localId);
  if (item) {
    item.syncStatus = 'failed';
    item.lastError = error;
    item.retryCount += 1;
    await db.put(EXPENSES_STORE, item);
  }
}

/** Sync all pending expenses to the backend */
export async function syncPendingExpenses(
  submitFn: (expense: Partial<Expense>) => Promise<boolean>
): Promise<{ synced: number; failed: number }> {
  const pending = await getPendingExpenses();
  let synced = 0;
  let failed = 0;

  for (const item of pending) {
    if (item.retryCount >= 3) {
      failed++;
      continue;
    }

    try {
      const success = await submitFn(item.expense);
      if (success) {
        await removeFromQueue(item.localId);
        synced++;
      } else {
        await markFailed(item.localId, 'Submission returned false');
        failed++;
      }
    } catch (err) {
      await markFailed(item.localId, err instanceof Error ? err.message : 'Unknown error');
      failed++;
    }
  }

  return { synced, failed };
}

/** Clear all queued items */
export async function clearQueue(): Promise<void> {
  const db = await getDB();
  await db.clear(EXPENSES_STORE);
  await db.clear(RECEIPTS_STORE);
}

/** Check if we're online and sync if we have pending items. Returns cleanup function. */
export function setupOnlineSync(submitFn: (expense: Partial<Expense>) => Promise<boolean>): () => void {
  const handler = async () => {
    const count = await getPendingCount();
    if (count > 0) {
      console.log(`[OfflineQueue] Back online. Syncing ${count} pending expenses...`);
      const result = await syncPendingExpenses(submitFn);
      console.log(`[OfflineQueue] Synced: ${result.synced}, Failed: ${result.failed}`);
    }
  };

  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}
